import { useApolloClient } from '@apollo/client';
import { ExpandedState, OnChangeFn } from '@tanstack/react-table';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AlarmIcon from '../../assets/icons/alarm.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
import TableFilterIcon from '../../assets/icons/table-filter.svg?react';
import { AlarmSidePanel } from '../../components/alarms';
import { Button } from '../../components/Button';
import { ResponsiveDataView } from '../../components/data-display/ResponsiveDataView';
import { AddAlarmDialog } from '../../components/dialogs/AddAlarmDialog';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { ActiveFilterTags } from '../../components/filters/ActiveFilterTags';
import { MobileAlarmsFilters } from '../../components/filters/MobileAlarmsFilters';
import { useAlarmFilterTags } from '../../components/filters/useAlarmFilterTags';
import { FiltersMobile } from '../../components/FiltersMobile';
import { IconButton } from '../../components/IconButton';
import { Toggle } from '../../components/Toggle';
import { useHeader } from '../../contexts/HeaderContext';
import { useMobileFilter } from '../../contexts/MobileFilterContext';
import {
  Alarm,
  useDeleteAlarmMutation,
  useDeleteAlertGroupMutation,
  useGetAlarmQuery,
  useGetCurrentUserQuery,
  useGetMachinesQuery,
  useSetAlarmEnabledMutation,
} from '../../gql/generated';
import { useAlarmFilters } from '../../hooks/useAlarmFilters';
import { AlarmListItemMobile } from './AlarmListItemMobile';
import { useAlarmsTable } from './useAlarmsTable';
import { logger } from '../../utils/logger';

import {
  AlarmTableRow,
  alarmRowAlarms,
  canEditAlarmRow,
  findAlarmGroupRow,
} from './alarmRows';
import { createAlarmColumns } from './createAlarmColumns';
import { useAlarmColumnFilters } from './useAlarmColumnFilters';

// ============================================================================
// ALARMS PAGE (Main Export)
// ============================================================================

const getAlarmSubRows = (row: AlarmTableRow) => row.members;

/** The open panel: the row it stands for, and the signal it narrows to. */
interface SelectedAlert {
  row: AlarmTableRow;
  signal?: string;
}

export function AlarmsPage() {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { alarmId: urlAlarmId } = useParams<{ alarmId?: string }>();
  const [searchParams] = useSearchParams();
  const state = useAlarmsTable();
  const { hasActiveFilters, clearFilters, showAllUsers, setShowAllUsers } =
    useAlarmFilters();

  // Only the creator edits or switches an alert. Other users' alerts appear
  // with "show all users" on and can only be deleted there (bug 166139).
  const { data: currentUserData } = useGetCurrentUserQuery({
    skip: !showAllUsers,
    errorPolicy: 'all',
  });
  const edmsUserId = currentUserData?.currentUser?.edmsUserId;
  const canEdit = useCallback(
    (row: AlarmTableRow) => canEditAlarmRow(row, { showAllUsers, edmsUserId }),
    [showAllUsers, edmsUserId]
  );
  const filterTags = useAlarmFilterTags();
  const { setMobileActionButton } = useHeader();
  const { toggle, close } = useMobileFilter();
  const [setAlarmEnabled] = useSetAlarmEnabledMutation();
  const [deleteAlarm] = useDeleteAlarmMutation();
  const [deleteAlertGroup] = useDeleteAlertGroupMutation();
  const [alarmDialogOpen, setAlarmDialogOpen] = useState(false);
  const [editingAlarm, setEditingAlarm] = useState<Alarm | null>(null);
  const [highlightedAlarmId, setHighlightedAlarmId] = useState<string | null>(
    null
  );
  const [selectedAlert, setSelectedAlert] = useState<SelectedAlert | null>(
    null
  );
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // A group row click puts the group id in the path, which the list resolves
  // on its own, without a GetAlarm round trip.
  const urlGroupRow = useMemo(
    () =>
      urlAlarmId
        ? state.allRows.find((row) => row.group && row.id === urlAlarmId)
        : undefined,
    [urlAlarmId, state.allRows]
  );

  // Fetch alarm from URL for deep linking
  const { data: urlAlarmData } = useGetAlarmQuery({
    variables: { id: urlAlarmId || '' },
    skip: !urlAlarmId || Boolean(urlGroupRow),
  });

  // Fetch the full machines list for the Add/Edit Pulse alert dialog.
  // Must be independent of the (possibly filtered/paginated) alarms list so
  // machines without any existing alarm are still selectable and resolve
  // correctly in the dialog's preview chart.
  const { data: allMachinesData } = useGetMachinesQuery({
    variables: { pagination: { limit: 1000, offset: 0 } },
    fetchPolicy: 'cache-and-network',
  });
  const allMachinesForDialog = useMemo(
    () =>
      (allMachinesData?.machines.rows || []).map((m) => ({
        id: m.id,
        displayName: m.displayName || m.machineId,
      })),
    [allMachinesData]
  );

  // A deep link names one alarm or one group. A signal of a grouped alert
  // opens the alert it belongs to, narrowed to that signal, the same as a
  // click in the table.
  useEffect(() => {
    if (!urlAlarmId) return;

    if (urlGroupRow) {
      setSelectedAlert({ row: urlGroupRow });
      return;
    }

    const alarm = urlAlarmData?.alarm as Alarm | undefined;
    if (!alarm) return;

    if (alarm.alertGroups?.length) {
      const groupRow = findAlarmGroupRow(state.allRows, alarm.id);
      if (groupRow) {
        setSelectedAlert({ row: groupRow, signal: alarm.signal });
        return;
      }
      // The list carries the other signals of the alert. Wait for it before the
      // single signal stands in for the whole alert.
      if (state.loading) return;
    }

    setSelectedAlert({ row: { id: alarm.id, alarm } });
  }, [
    urlAlarmId,
    urlGroupRow,
    urlAlarmData?.alarm,
    state.allRows,
    state.loading,
  ]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<AlarmTableRow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle highlight from URL - derived state, clear URL immediately
  const highlightId = searchParams.get('highlight');
  useEffect(() => {
    if (highlightId && highlightId !== highlightedAlarmId) {
      setHighlightedAlarmId(highlightId);
      navigate('/alarms', { replace: true });
    }
  }, [highlightId, highlightedAlarmId, navigate]);

  // Clear highlight after 3 seconds
  useEffect(() => {
    if (highlightedAlarmId) {
      const timer = setTimeout(() => {
        setHighlightedAlarmId(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedAlarmId]);

  // Get the column filter render function (reads filters directly from URL)
  const renderColumnFilter = useAlarmColumnFilters(state.data, state.loading);

  // Set up the filter button in the header for mobile
  useLayoutEffect(() => {
    setMobileActionButton(
      <div className='relative'>
        <IconButton
          aria-label='Toggle filters'
          icon={<TableFilterIcon width={24} height={24} />}
          onClick={toggle}
          className='hover:bg-transparent [&_path]:stroke-white'
        />
        {hasActiveFilters && (
          <div
            className='bg-focus border-surface absolute top-0 right-0 h-2.5
              w-2.5 rounded-full border-2'
          />
        )}
      </div>
    );

    return () => {
      setMobileActionButton(null);
    };
  }, [setMobileActionButton, toggle, hasActiveFilters]);

  // A deep link keeps the alarm in the path, so closing the panel it opened has
  // to drop it again.
  const clearAlarmUrl = useCallback(() => {
    if (!urlAlarmId) return;
    navigate(`/alarms${window.location.search}`, { replace: true });
  }, [navigate, urlAlarmId]);

  const refetchAlarms = useCallback(
    () =>
      apolloClient.refetchQueries({
        include: ['GetAlarms'],
      }),
    [apolloClient]
  );

  // A grouped alert switches as a whole. It reads as on while every signal is on.
  const handleToggleEnabled = useCallback(
    async (row: AlarmTableRow) => {
      const alarms = alarmRowAlarms(row);
      const isEnabled = !alarms.every((alarm) => alarm.isEnabled);

      try {
        // The mutations are independent, and each response updates only the
        // alarm's isEnabled in the cache, so a successful toggle needs no
        // refetch and cannot drop an alarm out of its group.
        await Promise.all(
          alarms
            .filter((alarm) => alarm.isEnabled !== isEnabled)
            .map((alarm) =>
              setAlarmEnabled({
                variables: { id: alarm.id, isEnabled },
                optimisticResponse: {
                  updateAlarm: { __typename: 'Alarm', id: alarm.id, isEnabled },
                },
              })
            )
        );
      } catch (error) {
        // Part of the group may have switched, so resync with what EDMS holds
        logger.error('Failed to toggle Pulse alert:', error);
        await refetchAlarms();
      }
    },
    [refetchAlarms, setAlarmEnabled]
  );

  const handleEdit = useCallback((row: AlarmTableRow) => {
    // A member alarm carries its group, so the wizard opens the whole alert
    setEditingAlarm(row.alarm);
    setAlarmDialogOpen(true);
  }, []);

  const handleAlarmCreated = useCallback(
    (alarmId: string) => {
      // Reset to page 1 to ensure the new alarm is visible (assuming newest first)
      state.setPageIndex(0);
      // Navigate to alarms page with highlight parameter
      navigate(`/alarms?highlight=${alarmId}`);
    },
    [navigate, state]
  );

  const handleDeleteClick = useCallback((row: AlarmTableRow) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!rowToDelete) return;

    setIsDeleting(true);
    try {
      const group = rowToDelete.group;
      if (group) {
        // The members go first. Its members carry no channels, so dropping the
        // group first would leave silent alerts behind when a member delete
        // fails; a failure here leaves the group intact and delete can run
        // again.
        await Promise.all(
          alarmRowAlarms(rowToDelete).map((alarm) =>
            deleteAlarm({ variables: { id: alarm.id } })
          )
        );
        await deleteAlertGroup({ variables: { id: group.id } });
      } else {
        await deleteAlarm({ variables: { id: rowToDelete.alarm.id } });
      }
    } catch (error) {
      logger.error('Failed to delete Pulse alert:', error);
    } finally {
      // Also after a failure: part of the alert may be gone, and the refetch
      // resyncs the table with what EDMS holds.
      await refetchAlarms();
      setIsDeleting(false);
      setRowToDelete(null);
    }
  }, [deleteAlarm, deleteAlertGroup, refetchAlarms, rowToDelete]);

  // Deleting says what actually goes: a whole grouped alert, one signal out of
  // a group, or a single alert.
  const deleteDescription = (() => {
    if (!rowToDelete) {
      return 'Are you sure you want to delete this alert? This action cannot be undone.';
    }
    const signalCount = alarmRowAlarms(rowToDelete).length;
    if (rowToDelete.group && signalCount > 1) {
      return `Deleting ${rowToDelete.group.name} removes the alert and all ${signalCount} of its signals. This action cannot be undone.`;
    }
    if (rowToDelete.groupName) {
      return `This alert is one signal in ${rowToDelete.groupName}. Deleting it removes that signal from the group, and the remaining signals keep notifying. This action cannot be undone.`;
    }
    return 'Are you sure you want to delete this alert? This action cannot be undone.';
  })();

  const handleDialogOpenChange = (open: boolean) => {
    setAlarmDialogOpen(open);
    // Clear editing alarm when dialog closes
    if (!open) {
      setEditingAlarm(null);
    }
  };

  const handleAddNewAlarm = () => {
    setEditingAlarm(null);
    setAlarmDialogOpen(true);
  };

  const columns = useMemo(
    () =>
      createAlarmColumns({
        onToggleEnabled: handleToggleEnabled,
        onEdit: handleEdit,
        onDelete: handleDeleteClick,
        canEdit,
      }),
    [handleToggleEnabled, handleEdit, handleDeleteClick, canEdit]
  );

  const handleClearFilters = useCallback(() => {
    clearFilters();
    close();
  }, [clearFilters, close]);

  // Different filters auto-open a different set of groups, so the manual
  // expands and collapses of the previous set no longer apply.
  const filterKey = JSON.stringify(state.filters);
  useEffect(() => {
    setExpandedRows({});
  }, [filterKey]);

  // A filter can match a member of a group, so every group opens while one is
  // active. An explicit collapse still wins.
  const expanded = useMemo(() => {
    if (!hasActiveFilters) return expandedRows;

    const openGroups: Record<string, boolean> = {};
    for (const row of state.rows) {
      if (row.members) openGroups[row.id] = true;
    }
    return { ...openGroups, ...expandedRows };
  }, [hasActiveFilters, expandedRows, state.rows]);

  const handleExpandedChange: OnChangeFn<ExpandedState> = useCallback(
    (updater) => {
      const next = typeof updater === 'function' ? updater(expanded) : updater;

      // TanStack's expand-all sentinel: every group row opens.
      if (next === true) {
        const openGroups: Record<string, boolean> = {};
        for (const row of state.allRows) {
          if (row.members) openGroups[row.id] = true;
        }
        setExpandedRows(openGroups);
        return;
      }

      // Keep only what this change toggled, so a filter's open default never
      // turns into persistent state for rows the user did not touch. A
      // collapse is kept as an explicit false, so the open default leaves it
      // closed.
      setExpandedRows((previous) => {
        const changes = { ...previous };
        const rowIds = new Set([
          ...Object.keys(expanded),
          ...Object.keys(next),
        ]);
        for (const rowId of rowIds) {
          const wasOpen = Boolean(expanded[rowId]);
          const isOpen = Boolean(next[rowId]);
          if (wasOpen !== isOpen) changes[rowId] = isOpen;
        }
        return changes;
      });
    },
    [expanded, state.allRows]
  );

  // A just-created alert is named by its alarm id; its row may carry the group id
  const isHighlighted = useCallback(
    (row: AlarmTableRow) =>
      row.id === highlightedAlarmId ||
      row.alarm.id === highlightedAlarmId ||
      (row.members?.some((member) => member.id === highlightedAlarmId) ??
        false),
    [highlightedAlarmId]
  );

  const rows = useMemo(
    () =>
      state.rows.map((row) => ({ ...row, __highlighted: isHighlighted(row) })),
    [state.rows, isHighlighted]
  );

  const selectedAlertAlarms = useMemo(
    () => (selectedAlert ? alarmRowAlarms(selectedAlert.row) : []),
    [selectedAlert]
  );

  return (
    <>
      {/* Add/Edit Alarm Dialog */}
      <AddAlarmDialog
        open={alarmDialogOpen}
        onOpenChange={handleDialogOpenChange}
        selectedMachineIds={[]}
        availableMachines={
          editingAlarm
            ? // Ensure the machines already on the alarm being edited are
              // always available, even if they were excluded from the
              // paginated machines list for some reason.
              Array.from(
                new Map(
                  [
                    ...allMachinesForDialog,
                    ...(editingAlarm.machines || []).map((m) => ({
                      id: m.machineId || m.id,
                      displayName: m.displayName || m.machineId || 'Unknown',
                    })),
                  ].map((m) => [m.id, m])
                ).values()
              )
            : allMachinesForDialog
        }
        editingAlarm={editingAlarm}
        onAlarmCreated={handleAlarmCreated}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title='Delete Pulse alert'
        description={deleteDescription}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        isNegative
        loading={isDeleting}
      />

      <AlarmSidePanel
        open={!!selectedAlert}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedAlert(null);
            clearAlarmUrl();
          }
        }}
        group={selectedAlert?.row.group ?? null}
        alarms={selectedAlertAlarms}
        signal={selectedAlert?.signal}
      />

      {/* Mobile Filter Panel */}
      <FiltersMobile
        onClear={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      >
        <MobileAlarmsFilters />
      </FiltersMobile>

      {/* Main Content */}
      <ResponsiveDataView
        data={rows}
        isLoading={state.loading}
        onRefresh={async () => {
          await state.refetch();
        }}
        error={state.error}
        getRowId={(row) => row.id}
        columns={columns}
        getSubRows={getAlarmSubRows}
        expanded={expanded}
        onExpandedChange={handleExpandedChange}
        pageIndex={state.pageIndex}
        pageSize={state.pageSize}
        onPageChange={state.setPageIndex}
        onPageSizeChange={state.setPageSize}
        sorting={state.sorting}
        onSortingChange={state.setSorting}
        columnPinning={state.columnPinning}
        onColumnPinningChange={state.setColumnPinning}
        columnVisibility={state.columnVisibility}
        onColumnVisibilityChange={state.setColumnVisibility}
        columnOrder={state.columnOrder}
        onColumnOrderChange={state.setColumnOrder}
        columnSizing={state.columnSizing}
        onColumnSizingChange={state.onColumnSizingChange}
        resetColumnSizing={state.resetColumnSizing}
        renderListItem={(row, config) => (
          <AlarmListItemMobile
            row={row}
            onToggleEnabled={handleToggleEnabled}
            canEdit={canEdit(row)}
            onClick={() => config.onClick?.(row)}
          />
        )}
        onRowClick={(row) => {
          if (row.group) {
            setSelectedAlert({ row });
          } else {
            // One signal of a grouped alert stands for the whole alert, so it
            // opens the same panel with the event log narrowed to that signal.
            const groupRow = findAlarmGroupRow(state.allRows, row.alarm.id);
            setSelectedAlert(
              groupRow ? { row: groupRow, signal: row.alarm.signal } : { row }
            );
          }

          // Every open panel lives in the path, so a refresh or a shared link
          // restores it. A group row goes in under its group id.
          navigate(`/alarms/${row.id}${window.location.search}`, {
            replace: true,
          });
        }}
        totalPages={state.totalPages}
        totalCount={state.totalCount}
        columnFilters={renderColumnFilter}
        activeFilters={
          hasActiveFilters ? (
            <ActiveFilterTags tags={filterTags} onClearAll={clearFilters} />
          ) : undefined
        }
        toolbarActions={
          <>
            <label className='flex items-center gap-2 whitespace-nowrap'>
              <Toggle
                checked={!showAllUsers}
                onCheckedChange={(checked) => setShowAllUsers(!checked)}
                aria-label='Only show my Pulse alerts'
              />
              <span className='size-p2 text-secondary'>Only my Alerts</span>
            </label>
            <Button
              variant='primary'
              leadingIcon={<PlusIcon />}
              onClick={handleAddNewAlarm}
            >
              Add Pulse alert
            </Button>
          </>
        }
        enableManageColumns
        emptyStateMessage='No Pulse alerts found. Try adjusting your filters.'
        ariaLabel='Pulse alerts'
        title='Pulse alerts'
        icon={<AlarmIcon />}
      />
    </>
  );
}
